import React, {useEffect, useState} from 'react';
import {View} from 'react-native';
import {Avatar, Card, ListItem, makeStyles} from '@rneui/themed';
import {observer} from 'mobx-react-lite';

import {useStores} from '../../../stores/use-stores';
import AddressModal from '../../checkout/AddressModal';
import SlotModal from '../../checkout/SlotModal';
import Text from '../../themed/Text';
import Switch from '../Switch';

const PickupDeliverComponent = observer(() => {
  const {cartStore, shopSelectionStore, userStore} = useStores();
  const isLoggedIn = userStore.isLoggedIn();
  const [touched, setTouched] = useState(false);

  const handlePickupPressed = () => {
    setTouched(true);
    cartStore.selectDay(null);
    cartStore.setPickup(true);
  };
  const handleDeliverPressed = () => {
    setTouched(true);
    cartStore.selectDay(null);
    cartStore.setDelivery(true);
  };

  const styles = useStyles();

  // These can be loaded right away, as long as we're logged in
  useEffect(() => {
    if (cartStore.isPickup && isLoggedIn) {
      cartStore.loadPickupSlots();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartStore.isPickup, isLoggedIn]);

  useEffect(() => {
    if (cartStore.isDelivery && cartStore.deliveryRegion?.id) {
      cartStore.loadDeliverySlots();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartStore.isDelivery, cartStore.deliveryRegion]);

  /**
   * default delivery address to user billing address
   * @param onAddressPress
   * @returns
   */

  useEffect(() => {
    if (
      cartStore.isDelivery &&
      !cartStore.deliveryAddress &&
      userStore.billingAddress
    ) {
      cartStore.setDeliveryAddress(userStore.billingAddress);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cartStore.isDelivery,
    cartStore.deliveryAddress,
    userStore.billingAddress,
  ]);

  const renderSlotModal = (onAddressPress?: () => void) => {
    return (
      <SlotModal methodTouched={touched}>
        {({handleOpen}) => (
          <ListItem
            onPress={() => {
              /**
               * if user presses time slot before address is set open the address first
               */
              cartStore.isDelivery &&
              !cartStore.deliveryAddress &&
              onAddressPress
                ? onAddressPress()
                : handleOpen();
            }}>
            <Avatar
              icon={{
                name: 'clock-outline',
                type: 'material-community',
                color: 'black',
                size: 18,
              }}
            />
            <ListItem.Content>
              <ListItem.Subtitle>
                <Text variant="caption" style={styles.subtitle}>
                  {cartStore.isDelivery ? `Delivery ` : `Pick up `}date and time
                </Text>
              </ListItem.Subtitle>
              <ListItem.Title>
                <Text variant="body2" style={styles.text}>
                  {pickupTime(
                    cartStore?.selectedSlot,
                    cartStore.isDelivery
                      ? 'Set a date for delivery'
                      : 'Set a date for pick up',
                  )}
                </Text>
              </ListItem.Title>
            </ListItem.Content>
            <ListItem.Chevron size={24} color={styles.text.color} />
          </ListItem>
        )}
      </SlotModal>
    );
  };

  return (
    <Card
      noMargin
      variant="button"
      style={styles.row}
      testID="checkout-screen-pickup-delivery-selection">
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 8,
        }}>
        <Text style={styles.cardTitle} variant="heading3">
          {cartStore.isPickup ? (
            'Pick up'
          ) : (
            <>
              {'Delivery '}
              <Text style={styles.cardTitleSecondary}>
                (available within 5km of store)
              </Text>
            </>
          )}
        </Text>
        <Switch
          disabled={
            !shopSelectionStore.canDeliver || !shopSelectionStore.canPickup
          }
          method={
            cartStore.isDelivery
              ? 'delivery'
              : cartStore.isPickup
              ? 'pickup'
              : undefined
          }
          onPress={method => {
            switch (method) {
              case 'delivery':
                handleDeliverPressed();
                break;
              case 'pickup':
                handlePickupPressed();
                break;
            }
          }}
        />
      </View>
      {cartStore.isDelivery ? (
        <>
          <View style={styles.section}>
            <Text style={styles.deliveryWarning}>
              The registered customer (18+) must be present at the delivery
              address to receive the order and show a valid form of ID to the
              driver.
            </Text>
          </View>
          <AddressModal addressType="delivery">
            {({handleOpen}) => (
              <>
                <ListItem onPress={handleOpen}>
                  <Avatar
                    icon={{
                      name: 'map-marker-outline',
                      type: 'material-community',
                      color: 'black',
                      size: 20,
                    }}
                  />
                  <ListItem.Content>
                    <ListItem.Subtitle>
                      <Text variant="caption" style={styles.subtitle}>
                        Delivery address
                      </Text>
                    </ListItem.Subtitle>
                    <ListItem.Title>
                      <Text variant="body2">
                        {`${cartStore.deliveryAddress?.lines?.[0]} ${cartStore.deliveryAddress?.locality}` ||
                          `Set address for delivery`}
                      </Text>
                    </ListItem.Title>
                  </ListItem.Content>
                  <ListItem.Chevron size={24} color={styles.text.color} />
                </ListItem>
                <Card.Divider />
                {renderSlotModal(handleOpen)}
              </>
            )}
          </AddressModal>
        </>
      ) : (
        <>{renderSlotModal()}</>
      )}
    </Card>
  );
});

function pickupTime(selectedSlot, placeholder) {
  let [from, to] = [
    new Date(selectedSlot?.dropOffWindow[0]),
    new Date(selectedSlot?.dropOffWindow[1]),
  ];
  const day = from.toLocaleString('en-AU', {month: 'short', day: 'numeric'});
  from = from.toLocaleString('en-AU', {timeStyle: 'short'});
  to = to.toLocaleString('en-AU', {timeStyle: 'short'});

  return selectedSlot ? `${day}, ${from}` : placeholder;
}

export default PickupDeliverComponent;

const useStyles = makeStyles(theme => {
  return {
    cardTitle: {color: theme.colors.black},
    cardTitleSecondary: {
      color: theme.colors.grey1,
      fontWeight: '400',
      fontSize: 12,
    },
    subtitle: {
      color: theme.colors.grey1,
    },
    text: {
      color: theme.colors?.legacy?.text?.heading?.primary,
    },
    row: {
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    mainButton: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: 4,
      paddingVertical: 8,
      flexGrow: 1,
      flexShrink: 1,
      flexBasis: '50%',
    },
    mainButtonSelected: {
      backgroundColor: theme.colors.legacy.background.button.primary,
    },
    mainButtonUnselected: {
      backgroundColor: 'white',
      borderWidth: 0.5,
      borderColor: theme.colors.legacy.uiControls.primary,
    },
    mainButtonText: {
      marginRight: 8,
      width: '50%',
      textAlign: 'center',
      fontSize: 16,
      fontWeight: '600',
    },
    mainButtonTextSelected: {
      color: 'white',
    },
    mainButtonTextUnselected: {
      color: theme.colors.legacy.text.heading.primary,
    },
    mainButtonIconHolder: {
      padding: 2,
      borderRadius: 4,
      backgroundColor: 'white',
    },
    mainButtonIcon: {
      backgroundColor: 'white',
    },
    section: {
      marginTop: 8,
      zIndex: 40,
    },
    label: {
      color: 'grey',
      fontSize: 12,
      marginBottom: 4,
    },
    deliveryWarning: {
      color: theme.colors.grey3,
      fontSize: 14,
    },
  };
});
